import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, makeObservable, observable, reaction } from "mobx";
import {
  AudioRecordingUploadingState,
  AudioUploadController,
  AudioUploadProgressStateStore,
  IAudioUploadingStore,
  UploadCompletionResult,
} from "@/store/audio/uploading/audio-uploading.types";
import { Uuid } from "@/domains/global/identifiers";
import { AudioUploadCompletionCoordinator } from "@/store/audio/uploading/audio-upload-completion-coordinator";
import { AudioRecordingUploadStatus } from "@/store/audio-recordings/types";
import { createRendererAudioUploadController } from "@/store/audio/uploading/create-renderer-audio-upload-controller";

type UploadingStoreConfig = {
  onUploadComplete?: (job: { audioRecordingId: Uuid }) => void;
};

export class AppStoreAudioUploadingStore
  extends AppSubStore
  implements IAudioUploadingStore, AudioUploadProgressStateStore
{
  private uploader: AudioUploadController;
  private uploadingStateByRecording = new Map<Uuid, AudioRecordingUploadingState>();
  private uploadCompletionCoordinator = new AudioUploadCompletionCoordinator();

  constructor(
    injectedDeps: AppSubStoreArgs & {
      config?: UploadingStoreConfig;
    }
  ) {
    super(injectedDeps);

    this.uploader = createRendererAudioUploadController({
      store: injectedDeps.store,
      progressStore: this,
      callbacks: {
        onUploadComplete: ({ job }) => {
          injectedDeps.config?.onUploadComplete?.(job);
          this.uploadCompletionCoordinator.resolve({
            audioRecordingId: job.audioRecordingId,
            result: {
              success: true,
            },
          });
        },
        onUploadError: ({ job, error }) => {
          this.uploadCompletionCoordinator.resolve({
            audioRecordingId: job.audioRecordingId,
            result: {
              success: false,
              error,
            },
          });
        },
      },
    });

    makeObservable<
      AppStoreAudioUploadingStore,
      | "uploader"
      | "uploadingStateByRecording"
      | "uploadCompletionCoordinator"
      | "getTerminalUploadResult"
    >(this, {
      uploader: observable,
      uploadingStateByRecording: observable.shallow,
      uploadCompletionCoordinator: false,
      enqueue: action,
      setProgress: action,
      clearProgress: action,
      resetState: action,
      getProgress: false,
      getTerminalUploadResult: false,
      waitForUploadComplete: false,
    });
  }

  async enqueue({
    audioRecordingId,
    forceRestart,
  }: {
    audioRecordingId: Uuid;
    forceRestart?: boolean;
  }): Promise<void> {
    this.uploadCompletionCoordinator.clearResult({ audioRecordingId });
    this.uploader.enqueue({
      job: {
        audioRecordingId,
        forceRestart,
      },
    });
  }

  resetState() {
    this.uploadingStateByRecording.clear();
    this.uploadCompletionCoordinator.rejectAll();
    this.uploader.resetState();
  }

  setProgress({
    audioRecordingId,
    state,
  }: {
    audioRecordingId: Uuid;
    state: AudioRecordingUploadingState;
  }): void {
    this.uploadingStateByRecording.set(audioRecordingId, state);
  }

  clearProgress({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.uploadingStateByRecording.delete(audioRecordingId);
  }

  getProgress({ audioRecordingId }: { audioRecordingId: Uuid }): AudioRecordingUploadingState {
    return (
      this.uploadingStateByRecording.get(audioRecordingId) ?? {
        uploadProgressPercent: null,
        isUploading: false,
        errorMessage: null,
      }
    );
  }

  getTerminalUploadResult({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): UploadCompletionResult | null {
    const recording = this.store.audioRecordings.get(audioRecordingId);

    if (recording?.uploadStatus === AudioRecordingUploadStatus.COMPLETED) {
      return { success: true };
    }

    if (recording?.uploadStatus === AudioRecordingUploadStatus.SKIPPED) {
      return { success: false, error: "Upload skipped" };
    }

    if (recording?.uploadStatus === AudioRecordingUploadStatus.ERROR) {
      return {
        success: false,
        error:
          recording.uploadErrorMessage ??
          this.getProgress({ audioRecordingId }).errorMessage ??
          "Upload failed",
      };
    }

    return null;
  }

  /**
   * Waits for a recording's source upload to finish.
   *
   * The durable recording row is the source of truth for terminal upload
   * states across refreshes, so this first checks persisted completion/error
   * before falling back to the in-memory completion coordinator.
   */
  waitForUploadComplete({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<UploadCompletionResult> {
    const terminalResult = this.getTerminalUploadResult({ audioRecordingId });
    if (terminalResult) {
      return Promise.resolve(terminalResult);
    }

    let disposeTerminalStatusReaction: (() => void) | null = reaction(
      () => this.getTerminalUploadResult({ audioRecordingId }),
      nextTerminalResult => {
        if (!nextTerminalResult) return;

        disposeTerminalStatusReaction?.();
        disposeTerminalStatusReaction = null;

        this.uploadCompletionCoordinator.resolve({
          audioRecordingId,
          result: nextTerminalResult,
        });
      },
      { fireImmediately: true }
    );

    return this.uploadCompletionCoordinator.waitFor({ audioRecordingId }).finally(() => {
      disposeTerminalStatusReaction?.();
      disposeTerminalStatusReaction = null;
    });
  }
}
